/**
 * ============================================================================
 * SystemLog 模块 - 常量定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义枚举标签映射（中文显示）
 * - 定义权限常量
 * - 定义业务常量
 * 
 * 📖 对应后端：
 * - Model: backend/app/Models/LoginAttempt.php (无静态属性)
 * - Permissions: backend/database/seeders/PermissionSeeder.php
 * 
 * ⚠️ 注意事项：
 * - 标签对象命名：{MODULE}_{ENUM}_LABELS（全大写）
 * - 权限常量命名：{MODULE}_PERMISSIONS（全大写）
 * - 使用 `as const` 保证类型推断
 * - 枚举标签的 key 必须与 enums.ts 中的枚举值对应
 * 
 * 🔄 更新时机：
 * - enums.ts 新增枚举值时（同步新增标签）
 * - 后端权限定义变化时
 * - 中文标签需要修改时
 * 
 * ✅ 验证方式：
 * 让AI执行以下检查：
 * 1. 读取 enums.ts，确认所有枚举都有对应的标签映射
 * 2. 读取 backend/database/seeders/PermissionSeeder.php，确认权限常量完整
 * 
 * @module types/system-log/constants
 * @generated Manual (AI-assisted)
 * @last-updated 2025-10-18
 * ============================================================================
 */

import { StatisticsPeriod, TrendGroupBy } from './enums'

/**
 * 统计周期标签映射
 */
export const STATISTICS_PERIOD_LABELS = {
  [StatisticsPeriod.TODAY]: '今天',
  [StatisticsPeriod.WEEK]: '本周',
  [StatisticsPeriod.MONTH]: '本月',
  [StatisticsPeriod.YEAR]: '本年',
} as const

/**
 * 趋势分组方式标签映射
 */
export const TREND_GROUP_BY_LABELS = {
  [TrendGroupBy.HOUR]: '按小时',
  [TrendGroupBy.DAY]: '按天',
} as const

/**
 * ⚠️ 系统日志（登录日志/错误日志）权限常量已迁移至统一权限管理
 * 
 * 请从 @bayes-mes/shared 导入标准权限常量：
 * - PERM_SYSTEM_LOGS_VIEW
 * - PERM_SYSTEM_LOGS_DELETE
 * 
 * @deprecated 请使用 constants/api-permissions.ts 中的权限常量
 * @see bayes-mes-shared/constants/api-permissions.ts
 */

/**
 * 默认查询参数
 */
export const DEFAULT_QUERY_PARAMS = {
  /** 默认页码 */
  page: 1,
  /** 默认每页数量 */
  per_page: 20,
  /** 默认统计周期 */
  period: StatisticsPeriod.TODAY,
  /** 默认分组方式 */
  groupBy: TrendGroupBy.HOUR,
  /** 默认异常日志查询时长（小时） */
  hours: 24,
} as const

/**
 * 登录限制常量
 * 
 * 对应后端：LoginAttempt::isIpLocked() 和 isUsernameLocked() 方法的默认参数
 */
export const LOGIN_RESTRICTIONS = {
  /** 最大失败尝试次数 */
  maxAttempts: 5,
  /** 锁定时间（分钟） */
  lockMinutes: 20,
} as const

